import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaCheck, FaInfoCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import i18n, { fetchLanguages } from "../../i18n";

const HIGHLIGHT = "#deaf77";

// Raw-format fallback matching TopBar — includes Chinese so it's never lost
const RAW_FALLBACK = [
  { Id: "2224", LookupDetail: "en", LookupDesc: "English" },
  { Id: "2225", LookupDetail: "id", LookupDesc: "Bahasa Indonesia" },
  { Id: "2226", LookupDetail: "cn", LookupDesc: "Chinese" },
];

const getLanguageCode = (language) => {
  const lookupDetail = language?.LookupDetail || "";
  const matchedCode = lookupDetail.match(/^(\S+)/)?.[1]?.toLowerCase() || "";
  if (matchedCode.startsWith("zh")) return "cn";
  if (matchedCode) return matchedCode;
  const id = String(language?.Id || language?.id || "");
  if (id === "2224") return "en";
  if (id === "2225") return "id";
  if (id === "2226") return "cn";
  return "";
};

const LanguageSettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  // Seed from i18n, which is what every visible string on the page is actually rendered with.
  // The storage keys are written by three different switchers and drift apart; trusting them
  // first is what made this screen tick English on a fully Indonesian page.
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const fromI18n = (i18n.language || "").split(/[-_]/)[0].toLowerCase();
    if (fromI18n.startsWith("zh")) return "cn";
    return fromI18n || localStorage.getItem("profileLanguage") || localStorage.getItem("language") || "en";
  });
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadLanguages = async () => {
      setIsLoadingLanguages(true);
      try {
        const apiLanguages = await fetchLanguages();

        // Merge API result with full fallback (same strategy as TopBar)
        // so Chinese is always present even though fetchLanguages filters it
        const merged = Array.isArray(apiLanguages) && apiLanguages.length
          ? [...apiLanguages]
          : [];

        const existingCodes = new Set(
          merged.map((l) => getLanguageCode(l)).filter(Boolean),
        );

        RAW_FALLBACK.forEach((fb) => {
          const code = getLanguageCode(fb);
          if (code && !existingCodes.has(code)) {
            merged.push(fb);
          }
        });

        const mapped = merged
          .map((language) => {
            const code = getLanguageCode(language);
            if (!code) return null;
            return {
              code,
              label: language?.LookupDesc || code.toUpperCase(),
              name: language?.LookupDesc || code.toUpperCase(),
              langId: String(language?.Id || language?.id || ""),
            };
          })
          .filter(Boolean);

        const unique = [];
        const seenCodes = new Set();
        mapped.forEach((language) => {
          if (!seenCodes.has(language.code)) {
            seenCodes.add(language.code);
            unique.push(language);
          }
        });

        if (mounted) setAvailableLanguages(unique.length ? unique : RAW_FALLBACK.map((fb) => ({
          code: getLanguageCode(fb),
          label: fb.LookupDesc,
          name: fb.LookupDesc,
          langId: String(fb.Id),
        })));
      } catch (error) {
        console.error("Failed to load languages:", error);
        if (mounted) setAvailableLanguages(RAW_FALLBACK.map((fb) => ({
          code: getLanguageCode(fb),
          label: fb.LookupDesc,
          name: fb.LookupDesc,
          langId: String(fb.Id),
        })));
      } finally {
        if (mounted) setIsLoadingLanguages(false);
      }
    };
    loadLanguages();
    return () => { mounted = false; };
  }, []);

  const getLanguageDisplayName = (lang) => {
    const code = (lang?.code || "").toLowerCase();
    if (code.startsWith("zh") || code === "cn") return "中文";
    if (code === "en") return t("languageSwitcher.english", { defaultValue: "English" });
    if (code === "id") return t("languageSwitcher.indonesian", { defaultValue: "Bahasa Indonesia" });
    return lang?.label || lang?.name || "";
  };

  const currentLanguage = useMemo(
    () => availableLanguages.find((l) => l.code === selectedLanguage),
    [availableLanguages, selectedLanguage],
  );

  const updateUserLanguage = async (langId, langDesc) => {
    try {
      const userId = localStorage.getItem("userID");
      if (!userId) return;
      await fetch("https://xianhefairy.com/LotusFairy_API/api/User/LanguageUpdate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ID: parseInt(userId), LANG_ID: langId, LANG_DESC: langDesc }),
      });
    } catch (error) {
      console.error("Error updating user language:", error);
    }
  };

  const handleLanguageChange = async (lang) => {
    if (!lang?.code || selectedLanguage === lang.code) return;

    setSelectedLanguage(lang.code);
    localStorage.setItem("language", lang.code);
    localStorage.setItem("profileLanguage", lang.code);
    if (lang.langId) localStorage.setItem("languageId", lang.langId);

    await i18n.changeLanguage(lang.code);
    await updateUserLanguage(lang.langId ? parseInt(lang.langId) : null, lang.name || lang.label);
    setIsSuccessVisible(true);
  };

  const handleSuccessClose = () => {
    setIsSuccessVisible(false);
    window.location.reload();
  };

  return (
    <div style={{ minHeight: "100vh", padding: isMobile ? "20px 14px 120px" : "34px 20px 160px" }}>
      <div
        style={{
          width: "100%",
          maxWidth: "680px",
          margin: "0 auto",
          fontFamily: "DMSans, sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", gap: "8px" }}>
          <button
            onClick={() => navigate(-1)}
            aria-label={t("account.back", { defaultValue: "Back" })}
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              border: "1px solid rgba(201,168,75,0.5)",
              background: "transparent",
              color: HIGHLIGHT,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <FaChevronLeft size={14} />
          </button>
          <h1
            style={{
              margin: 0,
              color: HIGHLIGHT,
              fontFamily: "DMSans-Bold, DMSans, sans-serif",
              fontSize: isMobile ? "24px" : "28px",
            }}
          >
            {t("languageSettings.title", { defaultValue: "Language" })}
          </h1>
        </div>

        {/* Hero card */}
        <div
          style={{
            borderRadius: "18px",
            border: "2px solid rgba(201,168,75,0.4)",
            background: "#0A031E",
            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
            padding: isMobile ? "18px" : "22px 24px",
            marginBottom: "16px",
          }}
        >
          <p style={{ margin: "0 0 4px", fontSize: "12px", color: "rgba(255,242,210,0.55)" }}>
            {t("languageSettings.currentLanguage", { defaultValue: "Current Language" })}
          </p>
          <p
            style={{
              margin: "0 0 6px",
              fontSize: isMobile ? "20px" : "22px",
              color: HIGHLIGHT,
              fontFamily: "DMSans-Bold, DMSans, sans-serif",
            }}
          >
            {getLanguageDisplayName(currentLanguage) ||
              t("languageSettings.title", { defaultValue: "Language" })}
          </p>
          <p style={{ margin: 0, fontSize: "13px", color: "rgba(255,242,210,0.55)" }}>
            {t("languageSettings.subtitle", {
              defaultValue: "Select your preferred display language",
            })}
          </p>
        </div>

        {/* Language list */}
        <div
          style={{
            borderRadius: "18px",
            border: "2px solid rgba(201,168,75,0.4)",
            background: "#0A031E",
            boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
            padding: isMobile ? "14px" : "16px",
            display: "grid",
            gap: "10px",
          }}
        >
          {isLoadingLanguages ? (
            <p style={{ margin: 0, color: "#D8CCA8", padding: "8px 0" }}>
              {t("account.loading", { defaultValue: "Loading..." })}
            </p>
          ) : (
            availableLanguages.map((lang) => {
              const isActive = selectedLanguage === lang.code;
              return (
                <button
                  key={`${lang.code}-${lang.langId}`}
                  onClick={() => handleLanguageChange(lang)}
                  style={{
                    width: "100%",
                    borderRadius: "14px",
                    border: isActive
                      ? `1px solid ${HIGHLIGHT}`
                      : "1px solid rgba(201,168,75,0.35)",
                    background: isActive ? "rgba(201,168,75,0.12)" : "#0d0520",
                    color: "#fff2d2",
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: isMobile ? "13px 14px" : "14px 16px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "border-color 0.2s, background 0.2s",
                  }}
                >
                  {/* Icon circle */}
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "50%",
                      border: isActive
                        ? `1.5px solid ${HIGHLIGHT}`
                        : "1.5px solid rgba(201,168,75,0.35)",
                      background: isActive ? "rgba(201,168,75,0.18)" : "rgba(201,168,75,0.06)",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FaChevronLeft
                      size={14}
                      color={HIGHLIGHT}
                      style={{ transform: "scaleX(-1)" }}
                    />
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: isMobile ? "15px" : "16px",
                        color: isActive ? HIGHLIGHT : "#fff2d2",
                        fontFamily: isActive
                          ? "DMSans-Bold, DMSans, sans-serif"
                          : "DMSans, sans-serif",
                      }}
                    >
                      {getLanguageDisplayName(lang)}
                    </div>
                    <div style={{ fontSize: "12px", color: "rgba(255,242,210,0.45)", marginTop: "2px" }}>
                      {lang.name}
                    </div>
                  </div>

                  {/* Right side */}
                  {isActive ? (
                    <span
                      style={{
                        fontSize: "12px",
                        color: HIGHLIGHT,
                        border: `1px solid ${HIGHLIGHT}`,
                        background: "rgba(201,168,75,0.14)",
                        borderRadius: "999px",
                        padding: "4px 10px",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {t("languageSettings.selected", { defaultValue: "Selected" })}
                    </span>
                  ) : (
                    <FaChevronLeft
                      size={13}
                      color="rgba(255,255,255,0.4)"
                      style={{ transform: "scaleX(-1)", flexShrink: 0 }}
                    />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer note */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "16px",
            padding: "0 4px",
          }}
        >
          <FaInfoCircle size={15} color={HIGHLIGHT} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: "13px", color: "rgba(255,242,210,0.55)" }}>
            {t("languageSettings.instantApply", {
              defaultValue: "Language changes apply instantly",
            })}
          </span>
        </div>
      </div>

      {/* Success modal */}
      {isSuccessVisible && (
        <div
          onClick={handleSuccessClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "grid",
            placeItems: "center",
            padding: "14px",
            zIndex: 2500,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "360px",
              borderRadius: "18px",
              border: "1px solid rgba(201,168,75,0.4)",
              background: "#171520",
              padding: "28px 24px 24px",
              color: "#fff2d2",
              fontFamily: "DMSans, sans-serif",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                background: "rgba(201,168,75,0.14)",
                border: `1.5px solid ${HIGHLIGHT}`,
                display: "grid",
                placeItems: "center",
              }}
            >
              <FaCheck size={22} color={HIGHLIGHT} />
            </div>
            <p
              style={{
                margin: 0,
                fontFamily: "DMSans-Bold, DMSans, sans-serif",
                fontSize: "18px",
                color: HIGHLIGHT,
              }}
            >
              {t("languageSettings.updateSuccessTitle", { defaultValue: "Language Updated" })}
            </p>
            <p style={{ margin: 0, fontSize: "14px", color: "rgba(255,242,210,0.7)" }}>
              {t("languageSettings.updateSuccessMessage", {
                defaultValue: "Your language preference has been saved.",
              })}
            </p>
            <button
              onClick={handleSuccessClose}
              style={{
                marginTop: "8px",
                width: "100%",
                borderRadius: "999px",
                border: `1px solid ${HIGHLIGHT}`,
                background: "rgba(201,168,75,0.14)",
                color: HIGHLIGHT,
                padding: "12px",
                cursor: "pointer",
                fontFamily: "DMSans-Bold, DMSans, sans-serif",
                fontSize: "15px",
              }}
            >
              {t("languageSettings.ok", { defaultValue: "OK" })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSettings;
